import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { toast } from '@/lib/v2/components';

import { UserData } from '@/src/application/hooks/useUserDataInterface';
import { AccountLimitResponse } from '@/src/infrastructure/interfaces/IResponses';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue } from '@/src/infrastructure/Protocol/useEmblue';
import { useGetContext } from '@/src/utils/ContextUtils';

interface StateContext {
  userData: UserData;
  canCreateGroups: boolean;
  accountLimitsData: AccountLimitResponse;
  isLoadingAccountLimits: boolean;
}

type TypeLimit = 'groups' | 'tags' | 'segments';

interface MutationContext {
  toggleAccountLimits: (type: TypeLimit, countTotal: number) => void;
}

interface ServiceProviderProps {
  children: ReactNode;
  userData: UserData;
}

const StateUserContext = createContext<StateContext | undefined>(undefined);
const MutationUserContext = createContext<MutationContext | undefined>(undefined);

const UserProvider = ({ children, userData }: ServiceProviderProps) => {
  const [canCreateGroups, setCanCreateGroups] = useState(true);

  const { companyId } = userData;

  const [accountLimits, errorAccountLimits, isLoadingAccountLimits] = useEmblue(
    ServiceMethods.getAccountLimits,
    Number(companyId)
  );

  useEffect(() => {
    if (!isLoadingAccountLimits && errorAccountLimits) {
      toast({
        variant: 'error',
        title: errorAccountLimits.message,
      });
    }
  }, [errorAccountLimits, isLoadingAccountLimits]);

  const accountLimitsData: AccountLimitResponse = useMemo(() => {
    if (!accountLimits) return {} as AccountLimitResponse;
    if ('success' in accountLimits && accountLimits.success) {
      return accountLimits.data;
    }
  }, [accountLimits]) as AccountLimitResponse;

  const toggleAccountLimits = useCallback((type: TypeLimit, countTotal: number) => {
    const accountLimitsSessionStr = sessionStorage.getItem('account_limits');
    if (accountLimitsSessionStr) {
      const { groupsLimit } = JSON.parse(accountLimitsSessionStr) as AccountLimitResponse;
      switch (type) {
        case 'groups': {
          const canCreate = countTotal < groupsLimit;
          setCanCreateGroups(canCreate);
          break;
        }
      }
    }
  }, []);

  const memoizedMutations = useMemo(
    () => ({
      toggleAccountLimits,
    }),
    [toggleAccountLimits]
  );

  return (
    <MutationUserContext.Provider value={memoizedMutations}>
      <StateUserContext.Provider
        value={{ userData, canCreateGroups, accountLimitsData, isLoadingAccountLimits }}
      >
        {children}
      </StateUserContext.Provider>
    </MutationUserContext.Provider>
  );
};

export const useStateUserContext = (): StateContext =>
  useGetContext<StateContext>(StateUserContext, 'UserProvider');

export const useMutationUserContext = (): MutationContext =>
  useGetContext<MutationContext>(MutationUserContext, 'UserProvider');

export default UserProvider;
